"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.IZiPool = exports.IZI_RIGHT_MOST_PT = exports.IZI_LEFT_MOST_PT = void 0;
const bignumber_1 = require("@ethersproject/bignumber");
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const util_1 = require("../../../util");
const BasePool_1 = require("../BasePool");
const BinarySearch_1 = require("./BinarySearch");
const LogPowMath_1 = require("./LogPowMath");
const Orders_1 = require("./Orders");
const SwapMath_1 = require("./SwapMath");
const SwapMathDesire_1 = require("./SwapMathDesire");
const BASE_GAS_CONSUMPTION = 70_000;
const STEP_GAS_CONSUMPTION = 30_000;
exports.IZI_LEFT_MOST_PT = -800000;
exports.IZI_RIGHT_MOST_PT = -exports.IZI_LEFT_MOST_PT;
class IZiPool extends BasePool_1.BasePool {
    constructor(address, token0, token1, fee, reserve0, reserve1, state, pointDelta, orders) {
        super(address, token0, token1, fee, reserve0, reserve1);
        this.state = state;
        this.pointDelta = pointDelta;
        this.orders = orders;
        this.rightMostPt = Math.floor(exports.IZI_RIGHT_MOST_PT / this.pointDelta) * this.pointDelta;
        this.leftMostPt = -this.rightMostPt;
        this.sqrtRateX96 = (0, LogPowMath_1.getSqrtPrice)(1);
    }
    coverCurrentPoint(orders, currentPt) {
        if (currentPt < orders.liquidityDeltaPoint[0])
            return false;
        const liquidityNum = orders.liquidityDeltaPoint.length;
        if (currentPt > orders.liquidityDeltaPoint[liquidityNum - 1])
            return false;
        return true;
    }
    findLeftCursor(orders, currentPoint) {
        const liquidityIdx = (0, BinarySearch_1.findLeft)(orders.liquidityDeltaPoint, currentPoint, BinarySearch_1.FindLeftOperator.LESS_THAN_OR_EQUAL);
        const sellingIdx = (0, BinarySearch_1.findLeft)(orders.sellingYPoint, currentPoint, BinarySearch_1.FindLeftOperator.LESS_THAN_OR_EQUAL);
        const isLiquidityPoint = (liquidityIdx >= 0) && (orders.liquidityDeltaPoint[liquidityIdx] === currentPoint);
        const isLimitOrderPoint = (sellingIdx >= 0) && (orders.sellingYPoint[sellingIdx] === currentPoint);
        return { liquidityIdx, sellingIdx, currentPoint, isLimitOrderPoint, isLiquidityPoint };
    }
    findRightCursor(orders, currentPoint) {
        const liquidityIdx = (0, BinarySearch_1.findLeft)(orders.liquidityDeltaPoint, currentPoint, BinarySearch_1.FindLeftOperator.LESS_THAN_OR_EQUAL);
        const sellingIdx = (0, BinarySearch_1.findRight)(orders.sellingXPoint, currentPoint, BinarySearch_1.FindRightOperator.GREATER_THAN_OR_EQUAL);
        const liquidityLength = orders.liquidityDeltaPoint.length;
        const isLiquidityPoint = (liquidityIdx < liquidityLength) && (orders.liquidityDeltaPoint[liquidityIdx] === currentPoint);
        const sellingLength = orders.sellingXPoint.length;
        const isLimitOrderPoint = (sellingIdx < sellingLength) && (orders.sellingXPoint[sellingIdx] === currentPoint);
        return { liquidityIdx, sellingIdx, currentPoint, isLimitOrderPoint, isLiquidityPoint };
    }
    getOutput(amountIn, direction) {
        let amountInBN = (0, util_1.getBigNumber)(amountIn);
        let amountX = 0;
        let amountY = 0;
        const st = { ...this.state };
        (0, tiny_invariant_1.default)(this.coverCurrentPoint(this.orders, st.currentPoint), 'IZI: CurrentOutOfRange');
        let finished = false;
        const sqrtRateX96 = this.sqrtRateX96;
        const pointDelta = this.pointDelta;
        let currentCursor = direction
            ? this.findLeftCursor(this.orders, st.currentPoint)
            : this.findRightCursor(this.orders, st.currentPoint);
        const fee = this.fee;
        const feeRemain = 1 - this.fee;
        let stepCounter = 0;
        if (direction) {
            while (this.leftMostPt <= st.currentPoint && !finished) {
                // clear limit order first
                if (currentCursor.isLimitOrderPoint) {
                    const amountNoFee = (0, util_1.getBigNumber)((0, util_1.getNumber)(amountInBN) * feeRemain);
                    if (amountNoFee.gt(0)) {
                        const currY = this.orders.sellingY[currentCursor.sellingIdx];
                        const { costX, acquireY } = (0, SwapMath_1.x2YAtPrice)(amountNoFee, st.sqrtPriceX96, currY);
                        if (acquireY.lt(currY) || costX.gte(amountNoFee))
                            finished = true;
                        let feeAmount = 0;
                        if (costX.gte(amountNoFee))
                            feeAmount = (0, util_1.getNumber)(amountInBN) - (0, util_1.getNumber)(costX);
                        else
                            feeAmount = (0, util_1.getNumber)(costX) * fee / feeRemain;
                        const cost = (0, util_1.getNumber)(costX) * feeAmount;
                        amountInBN = amountInBN.sub((0, util_1.getBigNumber)(cost));
                        amountX += cost;
                        amountY += (0, util_1.getNumber)(acquireY);
                    }
                    else {
                        finished = true;
                    }
                }
                if (finished)
                    break;
                const searchStart = st.currentPoint - 1;
                if (currentCursor.isLiquidityPoint) {
                    const amountNoFee = (0, util_1.getBigNumber)((0, util_1.getNumber)(amountInBN) * feeRemain);
                    if (amountNoFee.gt(0)) {
                        if (st.liquidity.gt(0)) {
                            const retState = (0, SwapMath_1.x2YRange)(st, st.currentPoint, sqrtRateX96, amountNoFee);
                            finished = retState.finished;
                            let feeAmount = 0;
                            if (retState.costX.gte(amountNoFee))
                                feeAmount = (0, util_1.getNumber)(amountInBN) - (0, util_1.getNumber)(retState.costX);
                            else
                                feeAmount = (0, util_1.getNumber)(retState.costX) * fee / feeRemain;
                            const cost = (0, util_1.getNumber)(retState.costX) + feeAmount;
                            amountX += cost;
                            amountY += (0, util_1.getNumber)(retState.acquireY);
                            amountInBN = amountInBN.sub((0, util_1.getBigNumber)(cost));
                            st.currentPoint = retState.finalPt;
                            st.sqrtPriceX96 = retState.sqrtFinalPriceX96;
                            st.liquidityX = retState.liquidityX;
                        }
                        if (!finished) {
                            st.currentPoint--;
                            if (st.currentPoint < this.leftMostPt)
                                break;
                            st.sqrtPriceX96 = (0, LogPowMath_1.getSqrtPrice)(st.currentPoint);
                            currentCursor = (0, Orders_1.findLeftFromCursor)(this.orders, currentCursor, st.currentPoint);
                            st.liquidity = this.orders.liquidity[currentCursor.liquidityIdx];
                            st.liquidityX = bignumber_1.BigNumber.from(0);
                        }
                    }
                }
                if (finished || st.currentPoint < this.leftMostPt)
                    break;
                currentCursor = (0, Orders_1.findLeftFromCursor)(this.orders, currentCursor, searchStart);
                const nextPt = Math.max(this.leftMostPt, (0, Orders_1.nearestLeftOneOrBoundary)(this.orders, currentCursor, searchStart, pointDelta));
                if (st.liquidity.eq(0)) {
                    st.currentPoint = nextPt;
                    st.sqrtPriceX96 = (0, LogPowMath_1.getSqrtPrice)(st.currentPoint);
                    currentCursor = (0, Orders_1.findLeftFromCursor)(this.orders, currentCursor, st.currentPoint);
                }
                else {
                    const amountNoFee = (0, util_1.getBigNumber)((0, util_1.getNumber)(amountInBN) * feeRemain);
                    if (amountNoFee.gt(0)) {
                        const retState = (0, SwapMath_1.x2YRange)(st, nextPt, sqrtRateX96, amountNoFee);
                        finished = retState.finished;
                        let feeAmount = 0;
                        if (retState.costX.gte(amountNoFee))
                            feeAmount = (0, util_1.getNumber)(amountInBN) - (0, util_1.getNumber)(retState.costX);
                        else
                            feeAmount = (0, util_1.getNumber)(retState.costX) * fee / feeRemain;
                        amountY += (0, util_1.getNumber)(retState.acquireY);
                        const cost = (0, util_1.getNumber)(retState.costX) + feeAmount;
                        amountX += cost;
                        amountInBN = amountInBN.sub((0, util_1.getBigNumber)(cost));
                        st.currentPoint = retState.finalPt;
                        st.sqrtPriceX96 = retState.sqrtFinalPriceX96;
                        st.liquidityX = retState.liquidityX;
                    }
                    else {
                        finished = true;
                    }
                    currentCursor = (0, Orders_1.findLeftFromCursor)(this.orders, currentCursor, st.currentPoint);
                    st.liquidity = this.orders.liquidity[currentCursor.liquidityIdx];
                }
                if (st.currentPoint <= this.leftMostPt)
                    break;
                ++stepCounter;
            }
        }
        else {
            while (st.currentPoint < this.rightMostPt && !finished) {
                if (currentCursor.isLimitOrderPoint) {
                    const amountNoFee = (0, util_1.getBigNumber)((0, util_1.getNumber)(amountInBN) * feeRemain);
                    if (amountNoFee.gt(0)) {
                        const currX = this.orders.sellingX[currentCursor.sellingIdx];
                        const { costY, acquireX } = (0, SwapMath_1.y2XAtPrice)(amountNoFee, st.sqrtPriceX96, currX);
                        if (acquireX < currX || costY.gte(amountNoFee))
                            finished = true;
                        let feeAmount = 0;
                        if (costY.gte(amountNoFee))
                            feeAmount = (0, util_1.getNumber)(amountInBN) - (0, util_1.getNumber)(costY);
                        else
                            feeAmount = (0, util_1.getNumber)(costY) * fee / feeRemain;
                        const cost = (0, util_1.getNumber)(costY) + feeAmount;
                        amountInBN = amountInBN.sub((0, util_1.getBigNumber)(cost));
                        amountY += cost;
                        amountX += (0, util_1.getNumber)(acquireX);
                    }
                    else {
                        finished = true;
                    }
                }
                if (finished)
                    break;
                const nextPoint = Math.min((0, Orders_1.nearestRightOneOrBoundary)(this.orders, currentCursor, st.currentPoint, pointDelta), this.rightMostPt);
                if (st.liquidity.eq(0)) {
                    st.currentPoint = nextPoint;
                    st.sqrtPriceX96 = (0, LogPowMath_1.getSqrtPrice)(st.currentPoint);
                    currentCursor = (0, Orders_1.findRightFromCursor)(this.orders, currentCursor, st.currentPoint);
                    st.liquidity = this.orders.liquidity[currentCursor.liquidityIdx];
                    st.liquidityX = st.liquidity;
                }
                else {
                    const amountNoFee = (0, util_1.getBigNumber)((0, util_1.getNumber)(amountInBN) * feeRemain);
                    if (amountNoFee.gt(0)) {
                        const retState = (0, SwapMath_1.y2XRange)(st, nextPoint, sqrtRateX96, amountNoFee);
                        finished = retState.finished;
                        let feeAmount = 0;
                        if (retState.costY.gte(amountNoFee))
                            feeAmount = (0, util_1.getNumber)(amountInBN) - (0, util_1.getNumber)(retState.costY);
                        else
                            feeAmount = (0, util_1.getNumber)(retState.costY) * fee / feeRemain;
                        const cost = (0, util_1.getNumber)(retState.costY) + feeAmount;
                        amountX += (0, util_1.getNumber)(retState.acquireX);
                        amountY += cost;
                        amountInBN = amountInBN.sub((0, util_1.getBigNumber)(cost));
                        st.currentPoint = retState.finalPt;
                        st.sqrtPriceX96 = retState.sqrtFinalPriceX96;
                        st.liquidityX = retState.liquidityX;
                    }
                    else {
                        finished = true;
                    }
                    if (st.currentPoint === nextPoint) {
                        currentCursor = (0, Orders_1.findRightFromCursor)(this.orders, currentCursor, st.currentPoint);
                        st.liquidity = this.orders.liquidity[currentCursor.liquidityIdx];
                        st.liquidityX = st.liquidity;
                    }
                    else {
                        // not necessary, because retState.finished must be true
                        finished = true;
                    }
                }
                ++stepCounter;
            }
        }
        const amountOut = direction ? amountY : amountX;
        const outTokenReserve = direction ? (0, util_1.getNumber)(this.reserve1) : (0, util_1.getNumber)(this.reserve0);
        if (amountOut >= outTokenReserve)
            return { output: outTokenReserve - 1, gasSpent: BASE_GAS_CONSUMPTION + STEP_GAS_CONSUMPTION * stepCounter };
        return { output: amountOut, gasSpent: BASE_GAS_CONSUMPTION + STEP_GAS_CONSUMPTION * stepCounter };
    }
    getInput(amountOut, direction) {
        let amountOutBN = (0, util_1.getBigNumber)(amountOut);
        const outTokenReserve = direction ? this.reserve1 : this.reserve0;
        if (amountOutBN.gte(outTokenReserve))
            return { input: Number.POSITIVE_INFINITY, gasSpent: this.swapGasCost };
        let amountX = 0;
        let amountY = 0;
        const st = { ...this.state };
        (0, tiny_invariant_1.default)(this.coverCurrentPoint(this.orders, st.currentPoint), 'IZI: CurrentOutOfRange');
        let finished = false;
        const sqrtRateX96 = this.sqrtRateX96;
        const pointDelta = this.pointDelta;
        let currentCursor = direction
            ? this.findLeftCursor(this.orders, st.currentPoint)
            : this.findRightCursor(this.orders, st.currentPoint);
        const fee = this.fee;
        const feeRemain = 1 - this.fee;
        let stepCounter = 0;
        if (direction) {
            while (this.leftMostPt <= st.currentPoint && !finished) {
                // clear limit order first
                if (currentCursor.isLimitOrderPoint) {
                    const currY = this.orders.sellingY[currentCursor.sellingIdx];
                    const { costX, acquireY } = (0, SwapMathDesire_1.x2YDesireAtPrice)(amountOutBN, st.sqrtPriceX96, currY);
                    if (acquireY.gte(amountOutBN))
                        finished = true;
                    const feeAmount = (0, util_1.getNumber)(costX) * fee / feeRemain;
                    const cost = (0, util_1.getNumber)(costX) + feeAmount;
                    amountOutBN = amountOutBN.sub(acquireY);
                    amountX += cost;
                    amountY += (0, util_1.getNumber)(acquireY);
                }
                if (finished)
                    break;
                const searchStart = st.currentPoint - 1;
                if (currentCursor.isLiquidityPoint) {
                    if (st.liquidity.gt(0)) {
                        const retState = (0, SwapMathDesire_1.x2YDesireRange)(st, st.currentPoint, sqrtRateX96, amountOutBN);
                        finished = retState.finished;
                        const feeAmount = (0, util_1.getNumber)(retState.costX) * fee / feeRemain;
                        const cost = (0, util_1.getNumber)(retState.costX) + feeAmount;
                        amountX += cost;
                        amountY += (0, util_1.getNumber)(retState.acquireY);
                        amountOutBN = amountOutBN.sub(retState.acquireY);
                        st.currentPoint = retState.finalPt;
                        st.sqrtPriceX96 = retState.sqrtFinalPriceX96;
                        st.liquidityX = retState.liquidityX;
                    }
                    if (!finished) {
                        st.currentPoint--;
                        if (st.currentPoint < this.leftMostPt)
                            break;
                        st.sqrtPriceX96 = (0, LogPowMath_1.getSqrtPrice)(st.currentPoint);
                        currentCursor = (0, Orders_1.findLeftFromCursor)(this.orders, currentCursor, st.currentPoint);
                        st.liquidity = this.orders.liquidity[currentCursor.liquidityIdx];
                        st.liquidityX = bignumber_1.BigNumber.from(0);
                    }
                }
                if (finished || st.currentPoint < this.leftMostPt)
                    break;
                currentCursor = (0, Orders_1.findLeftFromCursor)(this.orders, currentCursor, searchStart);
                const nextPt = Math.max(this.leftMostPt, (0, Orders_1.nearestLeftOneOrBoundary)(this.orders, currentCursor, searchStart, pointDelta));
                if (st.liquidity.eq(0)) {
                    st.currentPoint = nextPt;
                    st.sqrtPriceX96 = (0, LogPowMath_1.getSqrtPrice)(st.currentPoint);
                    currentCursor = (0, Orders_1.findLeftFromCursor)(this.orders, currentCursor, st.currentPoint);
                }
                else {
                    const retState = (0, SwapMathDesire_1.x2YDesireRange)(st, nextPt, sqrtRateX96, amountOutBN);
                    finished = retState.finished;
                    const feeAmount = (0, util_1.getNumber)(retState.costX) * fee / feeRemain;
                    amountY += (0, util_1.getNumber)(retState.acquireY);
                    const cost = (0, util_1.getNumber)(retState.costX) + feeAmount;
                    amountX += cost;
                    amountOutBN = amountOutBN.sub(retState.acquireY);
                    st.currentPoint = retState.finalPt;
                    st.sqrtPriceX96 = retState.sqrtFinalPriceX96;
                    st.liquidityX = retState.liquidityX;
                    currentCursor = (0, Orders_1.findLeftFromCursor)(this.orders, currentCursor, st.currentPoint);
                    st.liquidity = this.orders.liquidity[currentCursor.liquidityIdx];
                }
                if (st.currentPoint <= this.leftMostPt)
                    break;
                ++stepCounter;
            }
        }
        else {
            while (st.currentPoint < this.rightMostPt && !finished) {
                if (currentCursor.isLimitOrderPoint) {
                    const currX = this.orders.sellingX[currentCursor.sellingIdx];
                    const { costY, acquireX } = (0, SwapMathDesire_1.y2XDesireAtPrice)(amountOutBN, st.sqrtPriceX96, currX);
                    if (acquireX.gte(amountOutBN))
                        finished = true;
                    const feeAmount = (0, util_1.getNumber)(costY) * fee / feeRemain;
                    const cost = (0, util_1.getNumber)(costY) + feeAmount;
                    amountOutBN = amountOutBN.sub(acquireX);
                    amountY += cost;
                    amountX += (0, util_1.getNumber)(acquireX);
                }
                if (finished)
                    break;
                const nextPoint = Math.min((0, Orders_1.nearestRightOneOrBoundary)(this.orders, currentCursor, st.currentPoint, pointDelta), this.rightMostPt);
                if (st.liquidity.eq(0)) {
                    st.currentPoint = nextPoint;
                    st.sqrtPriceX96 = (0, LogPowMath_1.getSqrtPrice)(st.currentPoint);
                    currentCursor = (0, Orders_1.findRightFromCursor)(this.orders, currentCursor, st.currentPoint);
                    st.liquidity = this.orders.liquidity[currentCursor.liquidityIdx];
                    st.liquidityX = st.liquidity;
                }
                else {
                    const retState = (0, SwapMathDesire_1.y2XDesireRange)(st, nextPoint, sqrtRateX96, amountOutBN);
                    finished = retState.finished;
                    const feeAmount = (0, util_1.getNumber)(retState.costY) * fee / feeRemain;
                    const cost = (0, util_1.getNumber)(retState.costY) + feeAmount;
                    amountX += (0, util_1.getNumber)(retState.acquireX);
                    amountY += cost;
                    amountOutBN = amountOutBN.sub(retState.acquireX);
                    st.currentPoint = retState.finalPt;
                    st.sqrtPriceX96 = retState.sqrtFinalPriceX96;
                    st.liquidityX = retState.liquidityX;
                    if (st.currentPoint === nextPoint) {
                        currentCursor = (0, Orders_1.findRightFromCursor)(this.orders, currentCursor, st.currentPoint);
                        st.liquidity = this.orders.liquidity[currentCursor.liquidityIdx];
                        st.liquidityX = st.liquidity;
                    }
                    else {
                        // not necessary, because retState.finished must be true
                        finished = true;
                    }
                }
                ++stepCounter;
            }
        }
        return {
            input: direction ? amountX : amountY,
            gasSpent: BASE_GAS_CONSUMPTION + STEP_GAS_CONSUMPTION * stepCounter,
        };
    }
    calcCurrentPriceWithoutFee(direction) {
        const currentPrice = (0, util_1.getNumber)(this.state.sqrtPriceX96) / (0, util_1.getNumber)(SwapMath_1.two96);
        const p = currentPrice * currentPrice;
        return direction ? p : 1 / p;
    }
}
exports.IZiPool = IZiPool;
